import React, { FC, useState } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { useApi } from '../../hooks/storeHook'
import { useBoolState } from '../../hooks/commonHooks'
import { useMessages } from '../../hooks/snackbarHooks'
import { useSettingsPageStyles } from '../../hooks/settingsPageStylesHook'

import { makeStyles } from '@material-ui/core/styles'
import { Box, FormHelperText, FormLabel, Grid, Typography } from '@material-ui/core'

import { IOSSwitch } from '../IOSSwitch'
import { AccountProps, StoreAccountModel } from '../../types/accountProps'
import { PriceTypesModelBase } from '../../server/mpsklad_core/Models/PriceTypesModelBase'
import { integrationTypeNames } from '../../types/integrationTypeUtils'

export type MarketplacePricesSettingsProps<
  TAccount extends StoreAccountModel,
  TPriceModel extends PriceTypesModelBase
> = AccountProps<TAccount> & {
  onAddDefaultPrices: (accountId: number) => Promise<void>

  onForceUpdatePrices: (accountId: number) => Promise<void>

  setPriceTypes: (model: TPriceModel) => Promise<void>

  TopComponent?: FC<AccountProps<TAccount>>

  PriceTypesMatchingComponent: FC<
    AccountProps<TAccount>
    & {
    onChange: (model: TPriceModel) => Promise<void>
  }>
}

export const MarketplacePricesSettings =
  observer(
    <TAccount extends StoreAccountModel,
      TPriceModel extends PriceTypesModelBase>
    ({
       account,
       onAddDefaultPrices,
       onForceUpdatePrices,
       setPriceTypes,
       TopComponent,
       PriceTypesMatchingComponent
     }: MarketplacePricesSettingsProps<TAccount, TPriceModel>) => {
      const classes = useStyles()
      const pageClasses = useSettingsPageStyles()

      const api = useApi()
      const {showSuccess} = useMessages()

      const [isUpdate, setIsUpdate] = useState(account.isUpdatePrices)
      const [enabling, startEnable, endEnable] = useBoolState()
      const [forcing, startForce, endForce] = useBoolState()
      const isLoading = enabling || forcing

      const enableDefaults = async () => {
        try {
          startEnable()
          await onAddDefaultPrices(account.id)
          showSuccess('Активировано!')
        } finally {
          endEnable()
        }
      }

      const forceUpdate = async () => {
        try {
          startForce()
          await onForceUpdatePrices(account.id)
          showSuccess('Обновление в процессе…')
        } finally {
          endForce()
        }
      }

      const toggleAutoUpdate =
        async () => {
          const newIsUpdatePrices = !isUpdate

          await api.userSync.setIsUpdatePrices({
            accountId: account.id,
            integrationType: account.integrationType,
            isUpdatePrices: newIsUpdatePrices
          })

          setIsUpdate(newIsUpdatePrices)
          showSuccess(`Обновление цен ${(newIsUpdatePrices ? 'включено' : 'отключено')}!`)
        }

      return (
        <Box className={pageClasses.pageContainer}>
          <Typography className={pageClasses.header}>НАСТРОЙКИ ЦЕН</Typography>

          <Grid container spacing={3}>
            {
              TopComponent &&
              <Grid item xs={9}>
                <TopComponent account={account}/>
              </Grid>
            }

            <Grid item xs={9}>
              <PriceTypesMatchingComponent account={account} onChange={setPriceTypes}/>
            </Grid>

            <Grid item xs={9}>
              <button
                className={clsx(classes.wideButton, pageClasses.saveButton, 'default-button')}
                onClick={enableDefaults}
                disabled={isLoading}
              >
                <p>Добавить стандартные типы цен в МойСклад</p>
              </button>
            </Grid>

            <Grid item xs={9}>
              <Grid container className={clsx(pageClasses.settingsTopic, 'default-border')}>
                <Grid item xs={12} className={pageClasses.root}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Box>
                      <FormLabel className={pageClasses.boldLabel}>Автоматическое обновление цен</FormLabel>

                      <FormHelperText className={pageClasses.helperText}>
                        Обновление происходит в 10:00 по МСК
                      </FormHelperText>
                    </Box>

                    <IOSSwitch checked={isUpdate} onChange={toggleAutoUpdate}/>
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={9}>
              <Box width="313px">
                <button
                  className={clsx(pageClasses.saveButton, 'default-button')}
                  disabled={isLoading}
                  onClick={forceUpdate}
                >
                  <p>{isLoading ? 'Обновляем…' : 'Обновить цены'}</p>
                </button>

                <FormHelperText className={pageClasses.helperText}>
                  {/* TODO: Is this hint accurate? */}
                  Все цены с полем «Цена {integrationTypeNames[account.integrationType]}» 0 будут обновлены,
                  если соответствуют условиям
                </FormHelperText>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )
    })

const useStyles =
  makeStyles(() => ({
    wideButton: {
      width: 340,
      maxWidth: 340
    }
  }))